import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.5, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden bg-black" data-testid="hero-section">
      {/* Parallax Background — drone video (first 7 s loop) with poster fallback */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/gifcfzdk_DSC06849.webp"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src="/hero-drone.webm" type="video/webm" />
          <source src="/hero-drone.mp4" type="video/mp4" />
        </video>
        {/* Stronger dark wash so the headline is highly legible */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/50 to-black/90"></div>
        {/* Subtle blue tint for sporty feel */}
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-700/25 via-transparent to-sky-400/15"></div>
      </motion.div>

      {/* Animated grid overlay */}
      <div className="absolute inset-0 z-[1] opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(14, 165, 233, 0.2) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(14, 165, 233, 0.2) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      {/* Content */}
      <motion.div 
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-32 md:py-48 w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-5xl w-full"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6"
          >
            <div className="energy-bar w-24 mb-6"></div>
            <p className="overline text-blue-400 mb-2">
              WÄDENSWIL, SCHWEIZ
            </p>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="text-[1.75rem] xs:text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-[1.05] [word-break:keep-all] [overflow-wrap:normal] hyphens-none"
          >
            <span className="block">{t.hero.title}</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="text-lg sm:text-xl md:text-3xl font-bold mb-6 leading-tight"
          >
            <span className="gradient-text">{t.hero.subtitle}</span>
          </motion.p>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="text-lg md:text-xl text-gray-300 mb-12 max-w-2xl font-medium"
          >
            {t.hero.description}
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="flex flex-wrap gap-4"
          >
            <a 
              href="https://bookpadelarena.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="athletic-button athletic-button-primary"
              data-testid="hero-book-court-btn"
            >
              {t.hero.bookCourt}
              <ExternalLink className="w-5 h-5" />
            </a>
            <button 
              onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
              className="athletic-button athletic-button-outline"
            >
              {t.hero.learnMore}
            </button>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Animated scroll indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2"
        >
          <ChevronDown className="w-6 h-6 text-blue-400" />
          <div className="w-[2px] h-12 bg-gradient-to-b from-blue-400 to-transparent"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};
